import type {
  FmMapPanelDescriptor,
  FmMapWorkspaceDescriptor,
} from "./FmMapWorkspaceRoot.types";
import { validateMapWorkspaceActionShape } from "./FmMapWorkspaceRoot.shape";
import { validateSection } from "./sections/section.validation";
import { assertArchetypePaceCapabilities } from "./sections/paceCapabilities";
import {
  assertMapWorkspaceExactObject as exactObject,
  requireMapWorkspaceEnum,
  requireMapWorkspaceText,
  uniqueMapWorkspaceIds,
} from "./FmMapWorkspaceValidation.shared";

function validateLegendShape(
  panel: Extract<FmMapPanelDescriptor, { kind: "legend" }>,
  path: string,
): void {
  exactObject(
    panel.semantics,
    [
      "baseMapLabel", "fitMapLabel", "zoomInLabel", "zoomOutLabel",
      "legendLabel", "species", "speciesLabel", "speciesExplanation",
      "volumeLabel", "volumeExplanation", "maturityLabel",
      "maturityValueLabel", "maturityExplanation", "visibleLayerLabel",
      "hiddenLayerLabel", "featureLegend",
    ],
    `${path}.semantics`,
  );
  if (!Array.isArray(panel.semantics.species)) {
    throw new Error(
      `Invalid map workspace descriptor: ${path}.semantics.species must be an array.`,
    );
  }
  panel.semantics.species.forEach((species, index) => (
    exactObject(species, ["code", "label"], `${path}.semantics.species[${index}]`)
  ));
  panel.semantics.featureLegend?.entries.forEach((entry, index) => {
    exactObject(
      entry,
      ["status", "label"],
      `${path}.semantics.featureLegend.entries[${index}]`,
    );
    requireMapWorkspaceEnum(
      entry.status,
      ["auto", "auto_review", "manual", "unmapped"],
      `${path}.semantics.featureLegend.entries[${index}].status`,
    );
  });
}

function validateCollectionShape(
  panel: Extract<FmMapPanelDescriptor, { kind: "collection" }>,
  path: string,
): void {
  panel.records.forEach((record, recordIndex) => {
    const recordPath = `${path}.records[${recordIndex}]`;
    exactObject(
      record,
      ["id", "title", "description", "status", "metadata", "featureId", "actions"],
      recordPath,
    );
    record.metadata.forEach((item, index) => (
      exactObject(item, ["id", "label"], `${recordPath}.metadata[${index}]`)
    ));
    record.actions.forEach((action, index) => (
      validateMapWorkspaceActionShape(
        action,
        `${recordPath}.actions[${index}]`,
      )
    ));
  });
  panel.actions.forEach((action, index) => (
    validateMapWorkspaceActionShape(action, `${path}.actions[${index}]`)
  ));
  if (panel.primaryAction) {
    validateMapWorkspaceActionShape(panel.primaryAction, `${path}.primaryAction`);
  }
  if (panel.retainOnSelect !== undefined && typeof panel.retainOnSelect !== "boolean") {
    throw new Error(
      `Invalid map workspace descriptor: ${path}.retainOnSelect must be boolean.`,
    );
  }
}

function validatePanelShapes(descriptor: FmMapWorkspaceDescriptor): void {
  descriptor.panels.forEach((panel, index) => {
    const path = `panels[${index}]`;
    const payload = panel.kind === "legend"
      ? ["layerIds", "semantics"]
      : panel.kind === "filters"
        ? ["filterIds", "resetLabel"]
        : panel.kind === "collection"
          ? [
              "records", "selectedIds", "emptyLabel", "actionLabel",
              "overflowLabel", "actions", "primaryAction", "retainOnSelect",
            ]
          : panel.kind === "section"
            ? ["section"]
            : [];
    exactObject(panel, ["id", "title", "kind", ...payload], path);
    requireMapWorkspaceEnum(
      panel.kind,
      ["legend", "filters", "collection", "section"],
      `${path}.kind`,
    );
    if (panel.kind === "legend") validateLegendShape(panel, path);
    if (panel.kind === "collection") validateCollectionShape(panel, path);
    if (panel.kind === "section") {
      validateSection(panel.section, `${path}.section`);
      assertArchetypePaceCapabilities("FmMapWorkspaceRoot", panel.section, `${path}.section`);
    }
  });
}

function validatePanelCopy(panel: FmMapPanelDescriptor): void {
  requireMapWorkspaceText(panel.title, `panel "${panel.id}".title`);
  if (panel.kind === "filters")
    requireMapWorkspaceText(
      panel.resetLabel,
      `panel "${panel.id}".resetLabel`,
    );
  if (panel.kind === "collection") {
    requireMapWorkspaceText(
      panel.emptyLabel,
      `panel "${panel.id}".emptyLabel`,
    );
    requireMapWorkspaceText(
      panel.actionLabel,
      `panel "${panel.id}".actionLabel`,
    );
    requireMapWorkspaceText(
      panel.overflowLabel,
      `panel "${panel.id}".overflowLabel`,
    );
    panel.records.forEach((record) => {
      requireMapWorkspaceText(
        record.title,
        `panel "${panel.id}" record "${record.id}".title`,
      );
      record.metadata.forEach((item) =>
        requireMapWorkspaceText(
          item.label,
          `panel "${panel.id}" record "${record.id}" metadata "${item.id}"`,
        ),
      );
      record.actions.forEach((action) =>
        requireMapWorkspaceText(
          action.label,
          `panel "${panel.id}" record "${record.id}" action "${action.id}"`,
        ),
      );
    });
    panel.actions.forEach((action) =>
      requireMapWorkspaceText(
        action.label,
        `panel "${panel.id}" action "${action.id}"`,
      ),
    );
    if (panel.primaryAction) {
      requireMapWorkspaceText(
        panel.primaryAction.label,
        `panel "${panel.id}".primaryAction`,
      );
    }
  }
  if (panel.kind !== "legend") return;

  const path = `panel "${panel.id}".semantics`;
  const copy = panel.semantics;
  requireMapWorkspaceText(copy.baseMapLabel, `${path}.baseMapLabel`);
  requireMapWorkspaceText(copy.fitMapLabel, `${path}.fitMapLabel`);
  requireMapWorkspaceText(copy.zoomInLabel, `${path}.zoomInLabel`);
  requireMapWorkspaceText(copy.zoomOutLabel, `${path}.zoomOutLabel`);
  requireMapWorkspaceText(copy.legendLabel, `${path}.legendLabel`);
  requireMapWorkspaceText(copy.speciesLabel, `${path}.speciesLabel`);
  requireMapWorkspaceText(copy.speciesExplanation, `${path}.speciesExplanation`);
  requireMapWorkspaceText(copy.volumeLabel, `${path}.volumeLabel`);
  requireMapWorkspaceText(copy.volumeExplanation, `${path}.volumeExplanation`);
  requireMapWorkspaceText(copy.maturityLabel, `${path}.maturityLabel`);
  requireMapWorkspaceText(copy.maturityValueLabel, `${path}.maturityValueLabel`);
  requireMapWorkspaceText(copy.maturityExplanation, `${path}.maturityExplanation`);
  requireMapWorkspaceText(copy.visibleLayerLabel, `${path}.visibleLayerLabel`);
  requireMapWorkspaceText(copy.hiddenLayerLabel, `${path}.hiddenLayerLabel`);
  if (copy.featureLegend) {
    requireMapWorkspaceText(copy.featureLegend.title, `${path}.featureLegend.title`);
    const statuses = new Set<string>();
    copy.featureLegend.entries.forEach((entry, index) => {
      requireMapWorkspaceText(
        entry.label,
        `${path}.featureLegend.entries[${index}].label`,
      );
      if (statuses.has(entry.status)) {
        throw new Error(
          `Invalid map workspace descriptor: ${path}.featureLegend statuses must be unique.`,
        );
      }
      statuses.add(entry.status);
    });
  }

  if (!Array.isArray(copy.species)) {
    throw new Error(
      `Invalid map workspace descriptor: ${path}.species must be an array.`,
    );
  }
  const speciesCodes = new Set<string>();
  copy.species.forEach((species, index) => {
    requireMapWorkspaceText(species.code, `${path}.species[${index}].code`);
    requireMapWorkspaceText(species.label, `${path}.species[${index}].label`);
    if (speciesCodes.has(species.code)) {
      throw new Error(
        `Invalid map workspace descriptor: ${path}.species must not contain duplicate codes.`,
      );
    }
    speciesCodes.add(species.code);
  });
}

function validatePanelReferences(
  descriptor: FmMapWorkspaceDescriptor,
  filterIds: Set<string>,
  layerIds: Set<string>,
): void {
  descriptor.panels.forEach((panel) => {
    if (
      panel.kind === "legend" &&
      panel.layerIds.some((id) => !layerIds.has(id))
    ) {
      throw new Error(
        `Invalid map workspace descriptor: panel "${panel.id}" references an unknown layer.`,
      );
    }
    if (
      panel.kind === "filters" &&
      panel.filterIds.some((id) => !filterIds.has(id))
    ) {
      throw new Error(
        `Invalid map workspace descriptor: panel "${panel.id}" references an unknown filter.`,
      );
    }
    if (panel.kind === "collection") {
      const recordIds = new Set(panel.records.map((record) => record.id));
      if (recordIds.size !== panel.records.length) {
        throw new Error(
          `Invalid map workspace descriptor: panel "${panel.id}" records must be unique.`,
        );
      }
      if (panel.selectedIds.some((id) => !recordIds.has(id))) {
        throw new Error(
          `Invalid map workspace descriptor: panel "${panel.id}" selects an unknown record.`,
        );
      }
      if (
        panel.records.some(
          (record) =>
            record.featureId &&
            !descriptor.features.some(
              (feature) => feature.id === record.featureId,
            ),
        )
      ) {
        throw new Error(
          `Invalid map workspace descriptor: panel "${panel.id}" references an unknown feature.`,
        );
      }
    }
  });
}

export function validateMapWorkspacePanels(
  descriptor: FmMapWorkspaceDescriptor,
  filterIds: Set<string>,
  layerIds: Set<string>,
): void {
  validatePanelShapes(descriptor);
  const panelIds = uniqueMapWorkspaceIds(descriptor.panels, "panels");
  if (
    descriptor.activePanelId !== null &&
    !panelIds.has(descriptor.activePanelId)
  ) {
    throw new Error(
      "Invalid map workspace descriptor: activePanelId must reference exactly one registered panel.",
    );
  }
  descriptor.panels.forEach(validatePanelCopy);
  validatePanelReferences(descriptor, filterIds, layerIds);
}
